import { makeAutoObservable } from 'mobx';
import API, { ISettings, ITokenInformation, IUserConfigPackage } from '../API';
import PersonListState from './PersonListState';
import { PermissionsState } from '../components/pages/permissions/PermissionsList';

export default class PersonalSettingsState {
	public userConfig: IUserConfigPackage;

	public loading: boolean = false;
	public settings: ISettings;

	public admins: PersonListState;
	delegated: PermissionsState;
	app: PermissionsState;

	constructor(userConfig: IUserConfigPackage, settings: ISettings) {
		this.userConfig = userConfig;
		this.settings = settings;
		
		this.delegated = new PermissionsState(userConfig, false);
		this.app = new PermissionsState(userConfig, true);
		
		this.admins = new PersonListState(
			async (id) => {
				await new API(await userConfig.getToken()).addAdmin(id);
			},
			async (id) => {
				await new API(await userConfig.getToken()).removeAdmin(id);
			},
		);

		this.admins.setIds(settings?.administratorUserIds || []);
		makeAutoObservable(this);
	}

	public async ensureLoaded() {
		if (!this.settings && !this.loading) {
			this.load();
		}
	}

	public setUserId(id: string) {
		if (id) {
			this.admins.setMyId(id);
		}
	}

	setLoading(loading: boolean) {
		this.loading = loading;
	}

	public svcAccTokenInfo?: ITokenInformation;
	public setSvcAccTokenInfo(info: ITokenInformation | undefined) {
		this.svcAccTokenInfo = info;
	}

	public async ensureServiceAccountInfoLoaded() {
		let svcTokenInfo = await new API(await this.userConfig.getToken()).getTokenInformation(this.settings.serviceAccountId);
		this.setSvcAccTokenInfo(svcTokenInfo);
	}
	
	public async updateUseAppIdentity(newVal: boolean) {
		this.settings.useAppIdentity = newVal;
		this.setServiceAccountChangesMade(true);
	}

	public async updateServiceAccountId(newVal:string) {
		this.settings.serviceAccountId = newVal;
		this.ensureServiceAccountInfoLoaded();
		this.setServiceAccountChangesMade(true);
	}

	public async saveServiceAccountChanges() {
		await new API(await this.userConfig.getToken()).setServiceAccountMode(this.settings.useAppIdentity, this.settings.serviceAccountId);
		this.setServiceAccountChangesMade(false);
	}
	
	public serviceAccountChangesMade:boolean = false;
	public setServiceAccountChangesMade(newVal:boolean) {
		this.serviceAccountChangesMade = newVal;
	}

	public async updateEmailFilingUserId(newVal:string) {
		this.settings.emailFilingUserId = newVal;
		this.setEmailFilingChangesMade(true);
	}

	public async updateEmailFilingEnabled(newVal:boolean) {
		this.settings.emailFilingEnabled = newVal;
		this.setEmailFilingChangesMade(true);
	}

	public async updateEmailFilingDeleteOnFile(newVal:boolean) {
		this.settings.emailFilingDeleteOnFile = newVal;
		this.setEmailFilingChangesMade(true);
	}

	public async updateOutlookPreventAttachments(newVal:boolean) {
		this.settings.outlookPreventAttachments = newVal;
		this.setEmailFilingChangesMade(true);
	}

	public async saveEmailFilingChanges() {
		await new API(await this.userConfig.getToken()).setEmailAdminOptions(this.settings.emailFilingEnabled, this.settings.emailFilingUserId, this.settings.emailFilingDeleteOnFile, this.settings.outlookPreventAttachments);
		this.setEmailFilingChangesMade(false);
	}

	public emailFilingChangesMade:boolean = false;
	public setEmailFilingChangesMade(newVal:boolean) {
		this.emailFilingChangesMade = newVal;
	}

	public async load() {
		this.setLoading(true);

		let t = await this.userConfig.getToken();
		let settings: ISettings = await new API(t).getSettings();

		this.settings = settings;
		this.admins.setIds(settings.administratorUserIds);

		this.setLoading(false);
	}

}
